import React, { forwardRef, useEffect, useState, useRef } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import { mergeStyles } from "../../utils/utilStyles";
import Tooltip from "../../components/Tooltip";

const baseStyle = {
  backgroundColor: "bg-slate-200",
  hoverBackgroundColor: "bg-slate-100",
};

const AccordionHeader = forwardRef(
  (
    {
      isActive,
      prefixIcon,
      prefixIconSize = "size-6",
      suffixIcon,
      onClickSuffixIcon = () => {},
      title,
      subtitle,
      actionsChildren,
      validationMessage = "",
      overrideStyle = {},
      height = "",
    },
    ref
  ) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isWrapped, setIsWrapped] = useState(false);
    const isMobile = useIsMobile();
    const mergedStyle = mergeStyles(baseStyle, overrideStyle);
    const titleRef = useRef(null);

    useEffect(() => {
      if (isMobile) {
        return;
      }

      const handleMouseEnter = () => setIsHovering(true);
      const handleMouseLeave = () => setIsHovering(false);

      if (ref?.current) {
        ref.current.addEventListener("mouseenter", handleMouseEnter);
        ref.current.addEventListener("mouseleave", handleMouseLeave);
      }

      return () => {
        if (ref?.current) {
          ref.current.removeEventListener("mouseenter", handleMouseEnter);
          ref.current.removeEventListener("mouseleave", handleMouseLeave);
        }
      };
    }, [ref, isMobile]);

    useEffect(() => {
      const checkIfWrapped = () => {
        const titleElement = titleRef.current;
        if (titleElement) {
          const lineHeight = parseFloat(window.getComputedStyle(titleElement).lineHeight);
          const numberOfLines = Math.round(titleElement.scrollHeight / lineHeight);
          const isTextWrapped = numberOfLines > 1;
          setIsWrapped(isTextWrapped);
        }
      };

      // Check on mount
      checkIfWrapped();

      // Check on window resize
      window.addEventListener("resize", checkIfWrapped);

      // Cleanup
      return () => {
        window.removeEventListener("resize", checkIfWrapped);
      };
    }, [title]);

    return (
      <div
        ref={ref}
        className={`min-h-12 ${height} flex p-4 w-full shadow-md justify-between ${
          isWrapped ? "items-start" : "items-center"
        } gap-2
          ${isActive ? mergedStyle.backgroundColor : ""} 
          ${isHovering && !isActive ? mergedStyle.hoverBackgroundColor : ""}
        `}
      >
        <div className={`w-[80%] flex gap-2 justify-start ${isWrapped ? "items-start" : "items-center"}`}>
          {prefixIcon && <img src={prefixIcon} alt="prefix" className={`${prefixIconSize}`} />}
          <div className="flex flex-col md:flex-row justify-start items-start md:items-center md:gap-2">
            <h4
              ref={titleRef}
              className={`text-[12px] md:text-[14px] `}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </h4>
            {subtitle && (
              <h6
                className="text-[8px] md:text-[12px] text-gray-600"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {subtitle}
              </h6>
            )}
          </div>
          {suffixIcon && (
            <img onClick={onClickSuffixIcon} src={suffixIcon} alt="suffix" className="size-4 cursor-pointer" />
          )}
          {validationMessage && (
            <Tooltip
              text={validationMessage}
              direction={isMobile ? "bottom" : "right"}
            >
              <span className="text-red-500">*</span>
            </Tooltip>
          )}
        </div>
        <div className="flex mr-10 justify-start items-center">{(isHovering || isActive) && actionsChildren}</div>
      </div>
    );
  }
);

export default AccordionHeader;
